import React from 'react';
import { Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import RichContent from './RichContent';
import { resolveFileUrl } from '../services/api';

/** Featured Video block */
export const FeaturedVideoBlock = ({ config = {} }) => {
  const { title = '', subtitle = '', video_url = '' } = config;
  const embed = toEmbed(video_url);
  return (
    <section className="bg-[#e2ede6] py-16 lg:py-20" data-testid="home-block-featured-video">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {(title || subtitle) && (
          <div className="mb-8 text-center">
            {title && (
              <h2
                className="text-3xl lg:text-4xl text-[#0b1a2e]"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}
              >
                {title}
              </h2>
            )}
            {subtitle && <p className="mt-3 text-[#0b1a2e]/70">{subtitle}</p>}
          </div>
        )}
        <div className="rounded-2xl overflow-hidden shadow-xl aspect-video bg-black">
          {embed ? (
            <iframe
              src={embed}
              title={title || 'Featured video'}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="w-full h-full"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/60">
              <Play className="w-12 h-12" />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

/** Image Strip block */
export const ImageStripBlock = ({ config = {} }) => {
  const { title = '', images = [] } = config;
  const valid = images.filter((i) => i?.url);
  if (valid.length === 0) return null;
  return (
    <section className="bg-[#e2ede6] py-16 lg:py-20" data-testid="home-block-image-strip">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {title && (
          <h2
            className="mb-8 text-3xl lg:text-4xl text-[#0b1a2e]"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}
          >
            {title}
          </h2>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {valid.map((img, i) => (
            <div key={`${img.url}-${i}`} className="group relative aspect-square rounded-xl overflow-hidden bg-black/5">
              <img
                src={resolveFileUrl(img.url)}
                alt={img.caption || ''}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              {img.caption && (
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3 text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  {img.caption}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/** Text Block */
export const TextBlockBlock = ({ config = {} }) => {
  const { eyebrow = '', heading = '', body = '', align = 'center' } = config;
  const alignCls = align === 'left' ? 'text-left' : align === 'right' ? 'text-right' : 'text-center';
  return (
    <section className="bg-[#e2ede6] py-14 lg:py-20" data-testid="home-block-text">
      <div className={`max-w-3xl mx-auto px-6 lg:px-8 ${alignCls}`}>
        {eyebrow && (
          <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">
            {eyebrow}
          </span>
        )}
        {heading && (
          <h2
            className="mt-3 text-3xl lg:text-4xl text-[#0b1a2e]"
            style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}
          >
            {heading}
          </h2>
        )}
        {body && (
          <div className="mt-5">
            <RichContent text={body} />
          </div>
        )}
      </div>
    </section>
  );
};

/** CTA Banner */
export const CtaBannerBlock = ({ config = {} }) => {
  const { title = '', body = '', cta_label = 'Learn more', cta_href = '#', image_url = '' } = config;
  return (
    <section className="bg-[#e2ede6] py-16 lg:py-20" data-testid="home-block-cta">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="relative rounded-2xl overflow-hidden bg-[#0b1a2e] text-white">
          {image_url && (
            <img
              src={resolveFileUrl(image_url)}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-40"
            />
          )}
          <div className="relative p-8 lg:p-14 max-w-3xl">
            {title && (
              <h2
                className="text-3xl lg:text-4xl"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}
              >
                {title}
              </h2>
            )}
            {body && <p className="mt-4 text-white/80 leading-relaxed">{body}</p>}
            {cta_label && (
              cta_href?.startsWith('/') ? (
                <Link
                  to={cta_href}
                  className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0f5c3d] hover:bg-emerald-600 text-white font-medium transition"
                >
                  {cta_label}
                </Link>
              ) : (
                <a
                  href={cta_href}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0f5c3d] hover:bg-emerald-600 text-white font-medium transition"
                >
                  {cta_label}
                </a>
              )
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

function toEmbed(url) {
  if (!url) return '';
  // YouTube
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (yt) return `https://www.youtube-nocookie.com/embed/${yt[1]}`;
  // Vimeo
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return url;
}
